import { useEffect, useMemo, useState } from "react";
import { Upload, Plus, X } from "lucide-react";
import PillarsAudience from "./PillarsAudience";
import FeasibilityRing from "./FeasibilityRing";
import AdvancedTargetingDrawer from "./AdvancedTargetingDrawer";
import { humanizeTopic } from "./humanize";
import { api } from "../../lib/api";

/**
 * Step 3 — "Audience & Stimulus": Essential Pillars layout (v3, UI polish
 * refactor).
 *
 * Layout:
 *   1. Sourcing mix strip (unchanged).
 *   2. Ring left / Pillars right grid.
 *   3. Advanced Targeting card grid — every applied definition is its own
 *      chip; an always-visible "+ Add Definition" tile opens a right-side
 *      slide-in drawer with the full PureSpectrum attribute library. The
 *      raw "1,163 filters across 33 categories" noise is HIDDEN from the
 *      main wizard per the progressive-disclosure spec.
 *   4. Visual stimulus.
 */
export default function StepAudience({
  form, setForm,
  mixTotal,
  feasibility, feasibilityLoading, fmtCount,
  uploading, handleUpload,
}) {
  // --------- Shared demographics fetch ---------
  const [demographics, setDemographics] = useState(null);
  const [demographicsLoading, setDemographicsLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/campaigns/demographics");
        if (!cancelled) setDemographics(data);
      } catch (_e) {
        // Silent — drawer + pillars each degrade to a helpful empty state.
      } finally {
        if (!cancelled) setDemographicsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // --------- target_demographics writers ---------
  const setTargetDemographics = (nextTd) => {
    setForm({ ...form, target_demographics: { ...form.target_demographics, ...nextTd } });
  };
  const setCrQuotasOnly = (quotas) => {
    setForm({
      ...form,
      target_demographics: { ...form.target_demographics, cr_quotas: quotas },
    });
  };

  // --------- Advanced drawer state ---------
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Only ADVANCED (non-pillar) quotas appear as Definition tiles. All eight
  // essential-pillar demographic IDs (Age synthetic + Gender + Language +
  // Income + Relationship + Employment + Education + Device) are owned by
  // the Pillars component and must NOT double up as advanced tiles.
  const pillarIds = useMemo(() => {
    // Hard-coded PS qualification codes for the essential pillars (verified
    // against the staging catalog). Kept as string ids so the set membership
    // check matches the raw JSON coming back from PS.
    return new Set([
      "__age__",
      "211",   // Gender
      "1511",  // Language
      "1101",  // Household income
      "3195",  // Relationship / marital status
      "215",   // Employment
      "216",   // Education
      "219",   // Device
    ]);
  }, []);

  const advancedQuotas = (form.target_demographics?.cr_quotas || []).filter(
    (q) => !pillarIds.has(String(q.demographicId)),
  );

  // Look up a friendly label for each advanced quota from the fetched
  // catalog so tiles show "Household · 2 selected" instead of raw IDs.
  const definitionsMeta = useMemo(() => {
    if (!demographics?.flat) return advancedQuotas.map((q) => ({ ...q, label: `Filter ${q.demographicId}`, category: "Advanced" }));
    return advancedQuotas.map((q) => {
      const demo = demographics.flat.find((d) => String(d.demographicId) === String(q.demographicId));
      const label = humanizeTopic(demo?.question || "") || `Filter ${q.demographicId}`;
      return {
        ...q,
        label,
        category: demo?.category || "Advanced",
        count: (q.demographicOptionIds || []).length,
      };
    });
  }, [demographics, advancedQuotas]);

  const removeDefinition = (demographicId) => {
    setCrQuotasOnly(
      (form.target_demographics?.cr_quotas || []).filter(
        (q) => String(q.demographicId) !== String(demographicId),
      ),
    );
  };

  const target = parseInt(form.target_completes, 10) || 0;

  return (
    <div className="space-y-8" data-testid="step-audience">
      {/* ============ 1. SOURCING MIX STRIP ============ */}
      <section>
        <div className="text-xs uppercase tracking-[0.2em] font-bold mb-3">Audience sourcing mix</div>
        <div className="border border-[var(--rl-border)] bg-white">
          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[var(--rl-border)]">
            {[
              ["programmatic_cloudresearch", "Programmatic Panel Network", "global panel routing"],
              ["client_intercept", "Client Intercept", "your customer list"],
            ].map(([k, label, sublabel]) => (
              <div key={k} className="p-4 flex items-center gap-4">
                <div className="flex-1">
                  <div className="text-sm font-semibold text-[var(--rl-text)]">{label}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--rl-text-secondary)] font-mono mt-0.5">{sublabel}</div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={form.audience_sourcing_mix[k]}
                    onChange={(e) => setForm({
                      ...form,
                      audience_sourcing_mix: { ...form.audience_sourcing_mix, [k]: parseInt(e.target.value) || 0 },
                    })}
                    className="w-20 bg-white border border-[var(--rl-border)] px-3 py-2 focus:outline-none focus:border-[var(--rl-brand)] text-right font-mono"
                    data-testid={`mix-${k}`}
                  />
                  <span className="text-sm font-mono">%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={`mt-2 text-[10px] font-bold uppercase tracking-[0.25em] ${mixTotal === 100 ? "text-[var(--rl-success)]" : "text-[var(--rl-accent)]"}`} data-testid="mix-total">
          Total: {mixTotal}% {mixTotal !== 100 && "· must total 100%"}
        </div>
      </section>

      {/* ============ 2. FEASIBILITY RING + ESSENTIAL PILLARS ============ */}
      <section>
        <div className="text-xs uppercase tracking-[0.2em] font-bold mb-3">Essential audience pillars</div>
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
          {/* LEFT — ring */}
          <div className="border border-[var(--rl-border)] bg-[var(--rl-bg-subtle)] p-5 flex flex-col items-center">
            <FeasibilityRing
              eligible={feasibility?.total_eligible}
              target={target}
              loading={feasibilityLoading}
              testid="feasibility-ring"
            />
            <div className="mt-5 w-full grid grid-cols-2 gap-2" data-testid="reach-counter-breakdown">
              <StatBox
                label="Panel"
                fullLabel="Programmatic panel"
                value={feasibility ? fmtCount(feasibility.breakdown?.programmatic ?? feasibility.breakdown?.cloudresearch) : "—"}
                loading={feasibilityLoading && !feasibility}
                testid="stat-programmatic"
              />
              <StatBox
                label="Intercept"
                fullLabel="Client intercept"
                value={feasibility ? fmtCount(feasibility.breakdown?.intercept) : "—"}
                loading={feasibilityLoading && !feasibility}
                testid="stat-intercept"
              />
            </div>
            <div className="mt-3 pt-3 border-t border-[var(--rl-border)] w-full flex items-center justify-between text-[10px] uppercase tracking-[0.2em] font-mono">
              <span className="text-[var(--rl-text-secondary)]">Target</span>
              <span className="font-bold text-[var(--rl-text)]" data-testid="ring-target-value">{fmtCount(target)}</span>
            </div>
          </div>

          {/* RIGHT — pillars */}
          <div>
            <PillarsAudience
              demographicsData={demographics}
              demographicsLoading={demographicsLoading}
              target_demographics={form.target_demographics}
              onChange={setTargetDemographics}
            />
          </div>
        </div>
      </section>

      {/* ============ 3. ADVANCED TARGETING (progressive disclosure) ============ */}
      <section data-testid="advanced-targeting-section">
        <div className="flex items-baseline gap-3 mb-3">
          <div className="text-xs uppercase tracking-[0.2em] font-bold">Advanced targeting definitions</div>
          <span className="text-[10px] font-mono text-[var(--rl-text-secondary)]">Optional · beyond the essentials</span>
        </div>
        <div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
          data-testid="advanced-targeting-grid"
        >
          {/* Applied definitions — one tile each */}
          {definitionsMeta.map((def) => (
            <div
              key={def.demographicId}
              className="group relative border border-[var(--rl-text)] bg-white p-3 hover:shadow-[3px_3px_0_var(--rl-brand)] hover:-translate-y-px transition-all"
              data-testid={`definition-tile-${def.demographicId}`}
            >
              <div className="text-[9px] uppercase tracking-[0.25em] font-mono text-[var(--rl-text-secondary)] truncate">
                {def.category}
              </div>
              <div className="mt-1 text-sm font-semibold text-[var(--rl-text)] leading-snug line-clamp-2 min-h-[32px]">
                {def.label}
              </div>
              <div className="mt-2 flex items-center justify-between">
                <span
                  className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 bg-[var(--rl-brand)] text-white text-[10px] font-mono font-bold"
                  data-testid={`definition-tile-${def.demographicId}-count`}
                >
                  {def.count}
                </span>
                <button
                  type="button"
                  onClick={() => removeDefinition(def.demographicId)}
                  aria-label={`Remove ${def.label}`}
                  className="opacity-0 group-hover:opacity-100 inline-flex items-center justify-center w-6 h-6 border border-[var(--rl-border)] hover:border-[var(--rl-accent)] hover:text-[var(--rl-accent)] transition-all"
                  data-testid={`definition-tile-${def.demographicId}-remove`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
          {/* "+ Add Definition" empty slot — always present. */}
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="min-h-[104px] border-2 border-dashed border-[var(--rl-border)] bg-transparent flex flex-col items-center justify-center gap-1.5 text-[var(--rl-text-secondary)] hover:border-[var(--rl-brand)] hover:text-[var(--rl-brand)] hover:bg-white hover:-translate-y-px transition-all"
            data-testid="add-definition-tile"
          >
            <Plus className="w-5 h-5" />
            <span className="text-[11px] uppercase tracking-[0.25em] font-bold">Add Definition</span>
          </button>
        </div>
      </section>

      {/* ============ 4. VISUAL STIMULUS ============ */}
      <section>
        <div className="text-xs uppercase tracking-[0.2em] font-bold mb-3">Visual Stimulus (optional)</div>
        <div className="flex gap-3 flex-wrap mb-3">
          {["none", "image", "video", "figma"].map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setForm({ ...form, stimulus_type: t, stimulus_url: t === form.stimulus_type ? form.stimulus_url : null })}
              className={`px-4 py-2 text-sm border transition-colors ${form.stimulus_type === t ? "bg-[var(--rl-text)] text-white border-[var(--rl-text)]" : "border-[var(--rl-border)] hover:border-[var(--rl-text)]"}`}
              data-testid={`stimulus-type-${t}`}
            >
              {t === "none" ? "No stimulus" : t}
            </button>
          ))}
        </div>
        {(form.stimulus_type === "image" || form.stimulus_type === "video") && (
          <label className="inline-flex items-center gap-2 px-4 py-3 border-2 border-dashed border-[var(--rl-border)] cursor-pointer hover:border-[var(--rl-brand)] transition-colors" data-testid="stimulus-upload-label">
            <Upload className="w-4 h-4" />
            <span className="text-sm font-semibold">{uploading ? "Uploading…" : form.stimulus_url ? "Replace asset" : "Upload asset"}</span>
            <input type="file" accept={form.stimulus_type === "image" ? "image/*" : "video/*"} className="hidden" onChange={handleUpload} data-testid="stimulus-upload-input" />
          </label>
        )}
        {form.stimulus_type === "figma" && (
          <input
            value={form.stimulus_url || ""}
            onChange={(e) => setForm({ ...form, stimulus_url: e.target.value })}
            className="w-full bg-white border border-[var(--rl-border)] px-4 py-3 focus:outline-none focus:border-[var(--rl-brand)]"
            placeholder="https://www.figma.com/proto/…"
            data-testid="input-figma-url"
          />
        )}
        {form.stimulus_url && form.stimulus_type === "image" && (
          <img src={form.stimulus_url} alt="" className="mt-4 max-h-40 border border-[var(--rl-border)]" data-testid="stimulus-preview-image" />
        )}
      </section>

      {/* ============ ADVANCED TARGETING SLIDE-IN DRAWER ============ */}
      <AdvancedTargetingDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        value={form.target_demographics?.cr_quotas || []}
        onChange={setCrQuotasOnly}
        prefetchedData={demographics}
      />

      {/* Hidden compatibility markers for legacy e2e selectors. */}
      <div className="hidden" data-testid="reach-counter">
        <span data-testid="reach-counter-total">{feasibility?.total_eligible ?? ""}</span>
      </div>
    </div>
  );
}

/**
 * Compact stat cell — the label is capped in width + font-size so
 * "Programmatic" / "Intercept" always render fully inside their box,
 * even at the tight 2-column mobile layout under the ring.
 */
function StatBox({ label, fullLabel, value, loading, testid }) {
  return (
    <div className="bg-white border border-[var(--rl-border)] px-2 py-2 overflow-hidden" data-testid={testid}>
      <div
        title={fullLabel || label}
        className="text-[8px] uppercase tracking-[0.15em] font-bold text-[var(--rl-text-secondary)] truncate"
      >
        {label}
      </div>
      <div className="mt-1 font-display font-black text-base tabular-nums text-[var(--rl-text)] truncate">
        {loading ? "…" : value}
      </div>
    </div>
  );
}
